import {
  getDoc,
  getDocs,
  query,
  where,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore'
import {
  getAllCollectionEntries,
  getInstituteCollection,
  requireCollectionDocRef,
  resolveCollectionDocRef,
} from '@/services/firestorePaths'
import type {
  Class,
  ClassCreatePayload,
  ClassUpdatePayload,
} from '@/types'

interface ServiceError {
  code: string
  message: string
  originalError: unknown
}

function createServiceError(originalError: unknown): ServiceError {
  const err = originalError as { code?: string; message?: string }
  return {
    code: err.code ?? 'unknown',
    message: err.message ?? 'An unexpected error occurred',
    originalError,
  }
}

/**
 * Fetch a single Class document by ID from the `classes` collection.
 *
 * Returns the Class with its Firestore document ID merged in, or null
 * if no document exists. Firestore rules require the caller to be a
 * superuser, a tutor/manager whose institute matches `ownedBy`, or a
 * student in `attendeeIds`.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function getClass(id: string): Promise<Class | null> {
  try {
    const ref = await resolveCollectionDocRef('classes', id)
    if (!ref) return null
    const snap = await getDoc(ref)
    if (!snap.exists()) return null
    return { id: snap.id, ...snap.data() } as Class
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch all documents from the `classes` collection without filtering.
 *
 * Returns every Class document the authenticated user has read access to.
 * Firestore rules enforce `isOwned(resource)` — non-superuser callers
 * only receive classes where `ownedBy` matches one of their institutes.
 * **DEPRECATED:** Use `listAllClasses()` for superuser or `listClassesByOwner()`
 * for role-based filtering. This function is kept for backward compatibility.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listAllClasses(): Promise<Class[]> {
  try {
    const snap = await getDocs(getAllCollectionEntries('classes'))
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Class)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch classes by owner(s) — institutes.
 *
 * Queries `classes` with `where('ownedBy', 'in', ownedBy)`.
 * Returns classes visible to the current user. Firestore rules enforce
 * `isOwned(resource)` — non-superuser callers receive only classes
 * matching their institutes (note: classes do not have 'global' ownedBy).
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listClassesByOwner(ownedBy: string[]): Promise<Class[]> {
  try {
    const q = query(getAllCollectionEntries('classes'), where('ownedBy', 'in', ownedBy))
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Class)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch classes where `tutorId` matches the given tutor UID.
 *
 * Queries `classes` with `where('tutorId', '==', tutorId)`. Returns
 * classes visible to the current user per Firestore Security Rules.
 * Filters by the legacy `tutorId` field (lead tutor).
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listClassesByTutorId(tutorId: string): Promise<Class[]> {
  try {
    const q = query(getAllCollectionEntries('classes'), where('tutorId', '==', tutorId))
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Class)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch classes belonging to the given institute.
 *
 * Queries `classes` with `where('instituteId', '==', instituteId)`.
 * Returns classes visible to the current user per Firestore Security Rules.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listClassesByInstituteId(instituteId: string): Promise<Class[]> {
  try {
    const q = query(getAllCollectionEntries('classes'), where('instituteId', '==', instituteId))
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Class)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch classes teaching the given unit.
 *
 * Queries `classes` with `where('unitId', '==', unitId)`.
 * Returns classes visible to the current user per Firestore Security Rules.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listClassesByUnitId(unitId: string): Promise<Class[]> {
  try {
    const q = query(getAllCollectionEntries('classes'), where('unitId', '==', unitId))
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Class)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Fetch classes in which the given student is enrolled.
 *
 * Queries `classes` with `where('attendeeIds', 'array-contains', studentId)`.
 * Used by the student dashboard to show enrolled classes.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function listClassesByStudentId(studentId: string): Promise<Class[]> {
  try {
    const q = query(
      getAllCollectionEntries('classes'),
      where('attendeeIds', 'array-contains', studentId)
    )
    const snap = await getDocs(q)
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Class)
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Create a new Class document in the `classes` collection.
 *
 * Spreads the payload and stamps `createdAt` with `serverTimestamp()`.
 * Returns the auto-generated document ID. `ownedBy` must be an
 * instituteId (never 'global') — enforced by Firestore rules
 * (`!setsGlobalOwner() && requestOwnedByCallerInstitute()`).
 *
 * Throws ServiceError on Firestore failure.
 */
export async function createClass(payload: ClassCreatePayload): Promise<string> {
  try {
    const ref = await addDoc(getInstituteCollection('classes', payload.instituteId), {
      ...payload,
      createdAt: serverTimestamp(),
    })
    return ref.id
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Update an existing Class document by merging the given fields.
 *
 * Writes to `classes/{id}` via `updateDoc`. Only supplied fields are
 * overwritten. Firestore rules restrict updates to superuser or
 * tutor/manager whose institute matches the class's `ownedBy`.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function updateClass(
  id: string,
  payload: ClassUpdatePayload
): Promise<void> {
  try {
    await updateDoc(await requireCollectionDocRef('classes', id), { ...payload })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Delete a Class document from the `classes` collection by ID.
 *
 * Permanently removes the document. Firestore rules restrict deletion
 * to superuser only. Does not cascade — sessions, notes, and class tests
 * referencing this class remain.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function deleteClass(id: string): Promise<void> {
  try {
    await deleteDoc(await requireCollectionDocRef('classes', id))
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Add a student UID to the class's `attendeeIds` array via `arrayUnion`.
 *
 * Idempotent — enrolling an already-present student is a no-op.
 * Firestore rules restrict this to superuser or tutor/manager of the class.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function enrollStudentInClass(classId: string, studentId: string): Promise<void> {
  try {
    await updateDoc(await requireCollectionDocRef('classes', classId), {
      attendeeIds: arrayUnion(studentId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Remove a student UID from the class's `attendeeIds` array via `arrayRemove`.
 *
 * Idempotent — unenrolling an absent student is a no-op.
 * Firestore rules restrict this to superuser or tutor/manager of the class.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function unenrollStudentFromClass(classId: string, studentId: string): Promise<void> {
  try {
    await updateDoc(await requireCollectionDocRef('classes', classId), {
      attendeeIds: arrayRemove(studentId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Add a tutor UID to the class's `tutorIds` array via `arrayUnion`.
 *
 * Idempotent. Used by ClassDetailPage to associate additional tutors.
 * Firestore rules restrict this to superuser or existing tutor/manager.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function addTutorToClass(classId: string, tutorId: string): Promise<void> {
  try {
    await updateDoc(await requireCollectionDocRef('classes', classId), {
      tutorIds: arrayUnion(tutorId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Remove a tutor UID from the class's `tutorIds` array via `arrayRemove`.
 *
 * Idempotent. Firestore rules restrict this to superuser or existing
 * tutor/manager of the class.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function removeTutorFromClass(classId: string, tutorId: string): Promise<void> {
  try {
    await updateDoc(await requireCollectionDocRef('classes', classId), {
      tutorIds: arrayRemove(tutorId),
    })
  } catch (error) {
    throw createServiceError(error)
  }
}

/**
 * Set the lead tutor for a class by updating both `leadTutorId` and the
 * legacy `tutorId` field to keep them in sync.
 *
 * Writes to `classes/{classId}`. The lead tutor has primary responsibility
 * for the class. Firestore rules restrict this to superuser or existing
 * tutor/manager of the class.
 *
 * Throws ServiceError on Firestore failure.
 */
export async function setLeadTutor(classId: string, tutorId: string): Promise<void> {
  try {
    await updateDoc(await requireCollectionDocRef('classes', classId), {
      leadTutorId: tutorId,
      tutorId: tutorId, // keep backwards compat field in sync
    })
  } catch (error) {
    throw createServiceError(error)
  }
}